import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";

import {
  cachedUserId,
  generateClientId,
  type OfflineComplianceCompletion,
  type OfflineCompliancePhoto,
  offlineDb,
  requireCachedUserId,
} from "@/lib/offline/db";
import { syncService, type SyncStatus } from "@/lib/offline/syncService";

export type ChecklistResultValue = "PASS" | "FAIL" | "NOT_APPLICABLE";

export interface DisplayCompliancePhoto {
  id: string;
  fileName: string;
  mimeType: string;
  syncState: "pending" | "synced" | "error";
  blobUrl?: string;
  serverId?: string;
}

interface OfflineComplianceState {
  notes: string;
  checklistResults: Record<
    string,
    { result: ChecklistResultValue; notes?: string }
  >;
  externalServiceData: Record<string, unknown>;
  completion: OfflineComplianceCompletion | null;
  localPhotos: DisplayCompliancePhoto[];
  syncStatus: SyncStatus;
}

/**
 * Write-side hook for compliance task completion. Mirrors the
 * `useOfflineFollowUpActionCompletion` shape: per-field auto-save to IndexedDB,
 * photo enqueue, finalize triggers a finalize sync queue item that's gated on
 * any pending photos for *this* instance.
 *
 * Idempotency at the DB layer (Phase 2's partial unique index on the
 * compliance source columns) guarantees that double-fires don't produce
 * duplicate follow-up actions on the server side.
 */
export function useOfflineComplianceCompletion(
  instanceId: string,
  nurseryId: string,
) {
  const [state, setState] = useState<OfflineComplianceState>({
    notes: "",
    checklistResults: {},
    externalServiceData: {},
    completion: null,
    localPhotos: [],
    syncStatus: {
      pendingCount: 0,
      errorCount: 0,
      isSyncing: false,
      isOnline: typeof navigator !== "undefined" ? navigator.onLine : true,
    },
  });

  const blobUrlsRef = useRef<Set<string>>(new Set());

  // Hydrate from IndexedDB on mount and whenever instanceId changes.
  useEffect(() => {
    async function loadOfflineData() {
      const completion = await offlineDb.complianceCompletions.get(instanceId);
      const localPhotoRecords = await offlineDb.compliancePhotos
        .filter((p) => p.instanceId === instanceId)
        .toArray();

      const displayPhotos: DisplayCompliancePhoto[] = localPhotoRecords.map(
        (p) => {
          const blob = new Blob([p.data], { type: p.mimeType });
          const blobUrl = URL.createObjectURL(blob);
          blobUrlsRef.current.add(blobUrl);
          return {
            id: p.id,
            fileName: p.fileName,
            mimeType: p.mimeType,
            syncState: p.syncState,
            blobUrl,
            serverId: p.serverId,
          };
        },
      );

      const syncStatus = await syncService.getStatus();

      setState((prev) => ({
        ...prev,
        notes: completion?.notes ?? "",
        checklistResults: completion?.checklistResults ?? {},
        externalServiceData: completion?.externalServiceData ?? {},
        completion: completion ?? null,
        localPhotos: displayPhotos,
        syncStatus,
      }));
    }

    loadOfflineData();

    // When the sync queue ticks (a photo upload finishes, the finalize lands,
    // an item enters error state), re-read each known photo's syncState from
    // Dexie so the UI transitions from "Syncing…" → clean once the upload
    // succeeds. We patch syncState on existing entries rather than replacing
    // the array — that keeps blob URLs stable and avoids thumbnail flicker.
    const unsubscribe = syncService.subscribe(async (syncStatus) => {
      setState((prev) => ({ ...prev, syncStatus }));
      const rows = await offlineDb.compliancePhotos
        .filter((p) => p.instanceId === instanceId)
        .toArray();
      if (rows.length === 0) return;
      const stateById = new Map(rows.map((r) => [r.id, r.syncState]));
      setState((prev) => {
        let mutated = false;
        const nextPhotos = prev.localPhotos.map((p) => {
          const next = stateById.get(p.id);
          if (next && next !== p.syncState) {
            mutated = true;
            return { ...p, syncState: next };
          }
          return p;
        });
        return mutated ? { ...prev, localPhotos: nextPhotos } : prev;
      });
    });

    const handleOnline = () => {
      setState((prev) => ({
        ...prev,
        syncStatus: { ...prev.syncStatus, isOnline: true },
      }));
    };
    const handleOffline = () => {
      setState((prev) => ({
        ...prev,
        syncStatus: { ...prev.syncStatus, isOnline: false },
      }));
    };
    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    const blobUrls = blobUrlsRef.current;
    return () => {
      unsubscribe();
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
      blobUrls.forEach((url) => URL.revokeObjectURL(url));
      blobUrls.clear();
    };
  }, [instanceId]);

  // ---------- Persistence helpers ----------

  // Upsert the completion record. The `id` is the instanceId so re-renders
  // and re-mounts converge on the same row.
  const writeCompletion = useCallback(
    async (patch: Partial<OfflineComplianceCompletion>) => {
      // Re-stamped on every write, not just on create. The row is keyed by
      // instanceId with no user component, and logout now preserves it — so a
      // draft started by one practitioner can be picked up by whoever signs in
      // next. Stamping only on create left their work recorded as the first
      // practitioner's, which the privilege floor happily accepts between peers.
      const capturedByUserId = requireCachedUserId(await cachedUserId());

      // Read and write in one transaction. Unserialised, two concurrent calls
      // both saw no row and both called add(), the second rejecting with a
      // ConstraintError — which StrictMode's double-invoked state updaters made
      // reproducible on the first checklist tap of every fresh task, and which
      // rapid tapping reproduces in production. The losing patch was simply
      // dropped, and since the sync payload is rebuilt from this row rather
      // than from React state, a result lost on the last tap before submit
      // never reached the server at all.
      await offlineDb.transaction(
        "rw",
        offlineDb.complianceCompletions,
        async () => {
          const existing =
            await offlineDb.complianceCompletions.get(instanceId);
          if (existing) {
            await offlineDb.complianceCompletions.update(instanceId, {
              ...patch,
              capturedByUserId,
            });
          } else {
            await offlineDb.complianceCompletions.add({
              id: instanceId,
              nurseryId,
              notes: "",
              checklistResults: {},
              externalServiceData: {},
              startedAt: new Date().toISOString(),
              completedAt: null,
              syncState: "pending",
              ...patch,
              capturedByUserId,
            });
          }
        },
      );
    },
    [instanceId, nurseryId],
  );

  const upsertCompletion = useCallback(
    async (patch: Partial<OfflineComplianceCompletion>) => {
      try {
        await writeCompletion(patch);
      } catch (writeError) {
        // Reports and stops here. This is the path for writes nobody is waiting
        // on — field autosaves fired from onChange, where a rejection would
        // otherwise vanish and leave the screen showing work that was never
        // stored. Rethrowing instead put an unhandled rejection into those
        // handlers, and stacked a second toast on the one caller that does
        // catch. Callers that must fail loudly use `writeCompletion` directly.
        console.error(
          "[Offline] Failed to persist compliance draft",
          writeError,
        );
        toast.error(
          "Couldn't save that to this device. Check your connection and try again.",
        );
      }
    },
    [writeCompletion],
  );

  // ---------- Field setters ----------

  const setNotes = useCallback(
    async (notes: string) => {
      setState((prev) => ({ ...prev, notes }));
      await upsertCompletion({ notes });
    },
    [upsertCompletion],
  );

  const setChecklistItemResult = useCallback(
    async (
      itemDefinitionId: string,
      result: ChecklistResultValue,
      itemNotes?: string,
    ) => {
      setState((prev) => {
        const next = {
          ...prev.checklistResults,
          [itemDefinitionId]: { result, notes: itemNotes },
        };
        // Persist asynchronously; this fires inside the setState callback so
        // we capture the just-merged shape and write it back to IndexedDB.
        void upsertCompletion({ checklistResults: next });
        return { ...prev, checklistResults: next };
      });
    },
    [upsertCompletion],
  );

  const setExternalServiceField = useCallback(
    async (key: string, value: unknown) => {
      setState((prev) => {
        const next = { ...prev.externalServiceData, [key]: value };
        void upsertCompletion({ externalServiceData: next });
        return { ...prev, externalServiceData: next };
      });
    },
    [upsertCompletion],
  );

  // ---------- Evidence (photo) helpers ----------

  const savePhoto = useCallback(
    async (file: File) => {
      const arrayBuffer = await file.arrayBuffer();
      const photo: OfflineCompliancePhoto = {
        id: generateClientId(),
        instanceId,
        data: arrayBuffer,
        mimeType: file.type,
        fileName: file.name,
        syncState: "pending",
      };

      await offlineDb.compliancePhotos.add(photo);

      await offlineDb.syncQueue.add({
        id: generateClientId(),
        type: "compliancePhoto",
        entityId: photo.id,
        priority: 5,
        createdAt: Date.now(),
        attempts: 0,
      });

      const blob = new Blob([arrayBuffer], { type: file.type });
      const blobUrl = URL.createObjectURL(blob);
      blobUrlsRef.current.add(blobUrl);

      const displayPhoto: DisplayCompliancePhoto = {
        id: photo.id,
        fileName: photo.fileName,
        mimeType: photo.mimeType,
        syncState: photo.syncState,
        blobUrl,
      };

      setState((prev) => ({
        ...prev,
        localPhotos: [...prev.localPhotos, displayPhoto],
      }));

      // Make sure the completion row exists — the finalize handler reads it
      // by instanceId and we want to be sure it's there before any submit.
      await writeCompletion({});

      if (navigator.onLine) {
        syncService.debouncedSync();
      }
    },
    [instanceId, writeCompletion],
  );

  const deletePhoto = useCallback(async (photoId: string) => {
    await offlineDb.compliancePhotos.delete(photoId);
    await offlineDb.syncQueue.filter((q) => q.entityId === photoId).delete();

    setState((prev) => {
      const removed = prev.localPhotos.find((p) => p.id === photoId);
      if (removed?.blobUrl) {
        URL.revokeObjectURL(removed.blobUrl);
        blobUrlsRef.current.delete(removed.blobUrl);
      }
      return {
        ...prev,
        localPhotos: prev.localPhotos.filter((p) => p.id !== photoId),
      };
    });
  }, []);

  // ---------- Submit ----------

  const submitCompletion = useCallback(
    async (options?: { nextDueDate?: string; submitAsAwaiting?: boolean }) => {
      const completedAt = new Date().toISOString();

      await writeCompletion({
        completedAt,
        nextDueDate: options?.nextDueDate,
        submitAsAwaiting: options?.submitAsAwaiting,
        syncState: "pending",
        conflict: false,
        lastError: undefined,
      });

      await offlineDb.syncQueue.add({
        id: generateClientId(),
        type: "complianceFinalize",
        entityId: instanceId,
        priority: 6,
        createdAt: Date.now(),
        attempts: 0,
      });

      setState((prev) => ({
        ...prev,
        completion: prev.completion
          ? {
              ...prev.completion,
              completedAt,
              nextDueDate: options?.nextDueDate,
              submitAsAwaiting: options?.submitAsAwaiting,
              syncState: "pending",
            }
          : {
              id: instanceId,
              nurseryId,
              notes: prev.notes,
              checklistResults: prev.checklistResults,
              externalServiceData: prev.externalServiceData,
              nextDueDate: options?.nextDueDate,
              submitAsAwaiting: options?.submitAsAwaiting,
              startedAt: new Date().toISOString(),
              completedAt,
              syncState: "pending",
            },
      }));

      // When online, await the sync so callers can rely on the server having
      // received the completion before they invalidate query caches or
      // navigate away — otherwise the next list refetch races the queued POST
      // and shows stale server state until the next focus refresh.
      if (navigator.onLine) {
        await syncService.immediateSync();
      }

      // The sync records a rejection on the row, not by throwing, so re-read it:
      // a caller that only awaited the send would report success and navigate
      // away from a task the server never completed.
      const stored = await offlineDb.complianceCompletions.get(instanceId);
      if (stored?.conflict) {
        setState((prev) => ({ ...prev, completion: stored }));
        return { rejected: true };
      }
      return { rejected: false };
    },
    [instanceId, nurseryId, writeCompletion],
  );

  // Put a rejected submission back into draft so the user can correct it and
  // submit again. Their answers stay on the row untouched.
  const clearRejection = useCallback(async () => {
    const patch = {
      conflict: false,
      lastError: undefined,
      completedAt: null,
      syncState: "pending" as const,
    };
    await writeCompletion(patch);
    setState((prev) => ({
      ...prev,
      completion: prev.completion ? { ...prev.completion, ...patch } : null,
    }));
  }, [writeCompletion]);

  const retryFailed = useCallback(() => {
    syncService.retryFailed();
  }, []);

  // ---------- Derived state ----------

  const isLocallyCompleted = state.completion?.completedAt != null;
  const hasConflict = state.completion?.conflict === true;
  const rejectionReason = hasConflict ? state.completion?.lastError : undefined;
  const failureCount = useMemo(
    () =>
      Object.values(state.checklistResults).filter((r) => r.result === "FAIL")
        .length,
    [state.checklistResults],
  );
  const hasUploadedEvidence = state.localPhotos.some(
    (p) => p.syncState === "synced" || p.syncState === "pending",
  );

  return {
    // State
    notes: state.notes,
    checklistResults: state.checklistResults,
    externalServiceData: state.externalServiceData,
    photos: state.localPhotos,
    syncStatus: state.syncStatus,
    isLocallyCompleted,
    hasConflict,
    rejectionReason,
    failureCount,
    hasUploadedEvidence,
    // Setters
    setNotes,
    setChecklistItemResult,
    setExternalServiceField,
    // Photo helpers
    savePhoto,
    deletePhoto,
    // Submit
    submitCompletion,
    clearRejection,
    retryFailed,
  };
}
